import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { authApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthDialog({ open, onOpenChange, defaultTab = "signin" }) {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [view, setView] = useState("tabs"); // tabs | forgot | forgot-sent

  const [siEmail, setSiEmail] = useState("");
  const [siPassword, setSiPassword] = useState("");
  const [suEmail, setSuEmail] = useState("");
  const [suPassword, setSuPassword] = useState("");
  const [suName, setSuName] = useState("");
  const [fpEmail, setFpEmail] = useState("");
  const [fpDevUrl, setFpDevUrl] = useState("");

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const routeAfterAuth = (user) => {
    setUser(user);
    onOpenChange(false);
    nav(user.onboarded ? "/dashboard" : "/onboarding", { replace: true });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await authApi.login(siEmail.trim(), siPassword);
      toast.success("Welcome back ✦");
      routeAfterAuth(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (suPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      const { data } = await authApi.register(suEmail.trim(), suPassword, suName.trim());
      toast.success("Your crew is spinning up ✦");
      routeAfterAuth(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await authApi.forgot(fpEmail.trim());
      setFpDevUrl(data?.dev_reset_url || "");
      setView("forgot-sent");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resetDialogState = () => {
    setView("tabs"); setError(""); setFpEmail(""); setFpDevUrl("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-white/20 text-white max-w-md sm:rounded-3xl overflow-hidden">
        <div className="absolute inset-0 aurora opacity-40 pointer-events-none" />
        <div className="relative">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-neon-pink animate-pulse" />
              <span className="chrome-text">Sign in to SoloCrew</span>
            </DialogTitle>
          </DialogHeader>

          <Button
            data-testid="auth-google-btn"
            onClick={startGoogleAuth}
            className="mt-4 w-full rounded-full bg-white text-slate-900 hover:bg-neon-cyan font-bold py-6"
          >
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-white/15" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">or with email</span>
            <div className="flex-1 h-px bg-white/15" />
          </div>

          {view === "forgot" && (
            <form onSubmit={handleForgot} className="space-y-3" data-testid="forgot-form">
              <div className="font-display text-lg font-bold">Reset your password</div>
              <p className="text-sm text-white/70">Drop your email. We'll send a reset link that works for 15 minutes.</p>
              <Field icon={Mail}>
                <Input data-testid="forgot-email" type="email" required autoComplete="email"
                  value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} placeholder="you@yourthing.co"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12 pl-10" />
              </Field>
              {error && <ErrorNote message={error} />}
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={resetDialogState}
                  className="rounded-full text-white hover:bg-white/10" data-testid="forgot-back">
                  Back
                </Button>
                <Button data-testid="forgot-submit" type="submit" disabled={busy}
                  className="ml-auto rounded-full bg-neon-cyan hover:bg-neon-cyan/90 text-slate-900 font-bold px-6 neon-glow-cyan">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending…</> : "Send reset link"}
                </Button>
              </div>
            </form>
          )}

          {view === "forgot-sent" && (
            <div className="text-center py-4" data-testid="forgot-sent">
              <div className="w-14 h-14 mx-auto rounded-full bg-neon-cyan/20 flex items-center justify-center mb-3 neon-glow-cyan">
                <Mail className="w-7 h-7 text-neon-cyan" />
              </div>
              <div className="font-display text-xl font-bold mb-1">Check your inbox</div>
              <p className="text-sm text-white/70">
                If an account exists for <span className="font-mono text-white">{fpEmail}</span>, we just sent a reset link. It expires in 15 minutes.
              </p>
              <Button data-testid="forgot-done" onClick={resetDialogState}
                className="mt-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold">
                Back to sign in
              </Button>
            </div>
          )}

          {view === "tabs" && (
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
            <TabsList className="grid grid-cols-2 bg-white/5 rounded-full h-11 p-1 mb-3">
              <TabsTrigger data-testid="auth-tab-signin"
                value="signin"
                className="rounded-full data-[state=active]:bg-neon-pink data-[state=active]:text-white font-bold text-white/70"
              >
                Sign in
              </TabsTrigger>
              <TabsTrigger data-testid="auth-tab-signup"
                value="signup"
                className="rounded-full data-[state=active]:bg-neon-lime data-[state=active]:text-slate-900 font-bold text-white/70"
              >
                Sign up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={handleLogin} className="space-y-3">
                <Field icon={Mail}>
                  <Input data-testid="auth-signin-email" type="email" required autoComplete="email"
                    value={siEmail} onChange={(e) => setSiEmail(e.target.value)} placeholder="you@yourthing.co"
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12 pl-10" />
                </Field>
                <Field icon={Lock}>
                  <Input data-testid="auth-signin-password" type="password" required autoComplete="current-password"
                    value={siPassword} onChange={(e) => setSiPassword(e.target.value)} placeholder="password"
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12 pl-10" />
                </Field>
                {error && <ErrorNote message={error} />}
                <Button data-testid="auth-signin-submit" type="submit" disabled={busy}
                  className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 text-white font-bold py-6 neon-glow-pink">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Signing in…</> : "Sign in"}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    data-testid="auth-forgot-link"
                    onClick={() => { setError(""); setFpEmail(siEmail); setView("forgot"); }}
                    className="text-xs text-white/60 hover:text-neon-cyan underline underline-offset-4 decoration-white/20 font-mono uppercase tracking-widest"
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleRegister} className="space-y-3">
                <Field icon={UserIcon}>
                  <Input data-testid="auth-signup-name" required minLength={1} maxLength={80} autoComplete="name"
                    value={suName} onChange={(e) => setSuName(e.target.value)} placeholder="Your name"
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12 pl-10" />
                </Field>
                <Field icon={Mail}>
                  <Input data-testid="auth-signup-email" type="email" required autoComplete="email"
                    value={suEmail} onChange={(e) => setSuEmail(e.target.value)} placeholder="you@yourthing.co"
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12 pl-10" />
                </Field>
                <Field icon={Lock}>
                  <Input data-testid="auth-signup-password" type="password" required minLength={8} autoComplete="new-password"
                    value={suPassword} onChange={(e) => setSuPassword(e.target.value)} placeholder="at least 8 characters"
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12 pl-10" />
                </Field>
                {error && <ErrorNote message={error} />}
                <Button data-testid="auth-signup-submit" type="submit" disabled={busy}
                  className="w-full rounded-full bg-neon-lime hover:bg-neon-lime/90 text-slate-900 font-bold py-6 neon-glow-lime">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating your crew…</> : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          )}

          <p className="text-xs text-white/40 text-center mt-4 font-mono uppercase tracking-widest">
            ✦ no card required · cancel anytime ✦
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ icon: Icon, children }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
      {children}
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div data-testid="auth-error" className="rounded-xl border border-neon-pink/40 bg-neon-pink/10 text-neon-pink px-3 py-2 text-sm">
      {message}
    </div>
  );
}
